export type RegistroCorteAvance = {
  id: string
  modulo_id: string
  proyecto_id: string
  etapa_id: string
  cantidad_anterior: number
  cantidad_corte: number
  cantidad_acumulada: number
  cantidad_contratada: number
  avance_previo: number
  avance_nuevo: number
  quien_reporto: string
  fecha_corte: string
  foto_evidencia_url?: string | null
  observaciones?: string
  acta_corte_id?: string | null
}

export type ItemActaCorte = {
  modulo_id: string
  codigo: string
  nombre: string
  unidad: string
  cantidad_contratada: number
  cantidad_anterior: number
  cantidad_este_corte: number
  cantidad_acumulada: number
  saldo_por_ejecutar: number
  porcentaje_ejecutado: number
  precio_unitario: number
  subtotal_corte: number
}

export type CapituloActaCorte = {
  etapa_id: string
  orden: number
  nombre: string
  items: ItemActaCorte[]
  subtotal_capitulo: number
}

export type EstadoActaCorte = 'borrador' | 'radicada' | 'aprobada' | 'cobrada' | 'anulada'

export type ActaCorteObra = {
  id: string
  consecutivo: string
  proyecto_id: string
  numero_corte: number
  fecha_corte: string
  fecha_inicio_periodo?: string
  fecha_fin_periodo?: string
  residente_nombre: string
  interventor_nombre?: string
  cliente_nombre: string
  estado: EstadoActaCorte
  valor_costo_directo: number
  valor_aiu: number
  valor_total: number
  fecha_radicacion?: string | null
  fecha_cobro?: string | null
  dias_credito: number
  fecha_vencimiento?: string | null
  detalles_capitulos: CapituloActaCorte[]
  notas?: string
  activo: boolean
  created_at: string
  updated_at: string
}

/**
 * Calcula el estado de semáforo de atraso de una actividad u obra.
 * Compara el avance físico real reportado contra el avance esperado según cronograma.
 */
export function calcularSemaforoAtraso(
  avanceReal: number,
  fechaInicio?: string | null,
  fechaFin?: string | null
): { estado: 'al_dia' | 'alerta_atraso' | 'critico'; atrasoPct: number; esperadoPct: number } {
  if (!fechaInicio || !fechaFin) {
    return { estado: 'al_dia', atrasoPct: 0, esperadoPct: 0 }
  }

  // 1. Si ya se completó al 100%
  if (avanceReal >= 100) {
    return { estado: 'al_dia', atrasoPct: 0, esperadoPct: 100 }
  }

  // 2. Normalizar fecha actual en formato local YYYY-MM-DD (sin desfases de zona horaria UTC)
  const d = new Date()
  const hoyStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  // Si la fecha de inicio es futura (mayor a hoy), la actividad aún no arranca -> AL DÍA
  if (fechaInicio > hoyStr) {
    return { estado: 'al_dia', atrasoPct: 0, esperadoPct: 0 }
  }

  // 3. Parsear a medianoche local
  const [y1, m1, d1] = fechaInicio.split('-').map(Number)
  const ini = new Date(y1, m1 - 1, d1, 0, 0, 0, 0).getTime()
  const [y2, m2, d2] = fechaFin.split('-').map(Number)
  const fin = new Date(y2, m2 - 1, d2, 23, 59, 59, 999).getTime()
  const hoy = new Date().setHours(0, 0, 0, 0)

  if (hoy <= ini) {
    return { estado: 'al_dia', atrasoPct: 0, esperadoPct: 0 }
  }

  const duracionTotal = fin - ini
  if (duracionTotal <= 0) {
    return { estado: 'al_dia', atrasoPct: 0, esperadoPct: 100 }
  }

  const tiempoTranscurrido = Math.min(duracionTotal, hoy - ini)
  const esperadoPct = Math.min(100, Math.round((tiempoTranscurrido / duracionTotal) * 100))
  const atrasoPct = Math.max(0, esperadoPct - avanceReal)

  // 4. Si la fecha de entrega aún está en el futuro (fechaFin >= hoyStr) y el atraso es incipiente o tolerable
  if (fechaFin >= hoyStr && (atrasoPct <= 15 || esperadoPct <= 15)) {
    return { estado: 'al_dia', atrasoPct: 0, esperadoPct }
  }

  if (atrasoPct <= 0) {
    return { estado: 'al_dia', atrasoPct: 0, esperadoPct }
  } else if (atrasoPct <= 20) {
    return { estado: 'alerta_atraso', atrasoPct, esperadoPct }
  } else {
    return { estado: 'critico', atrasoPct, esperadoPct }
  }
}
